import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "../ui/card";
import { Clock, ArrowRight } from "lucide-react";

const STORAGE_KEY = "recently_viewed_products";
const MAX_ITEMS = 6;

// Add product to recently viewed
export const addToRecentlyViewed = (product) => {
  if (!product?.product_id) return;
  
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    let items = stored ? JSON.parse(stored) : [];
    
    // Remove if already exists
    items = items.filter(p => p.product_id !== product.product_id);
    
    // Add to beginning
    items.unshift({
      product_id: product.product_id,
      title: product.title,
      image: product.images?.[0] || product.image,
      price: product.base_price || product.variants?.[0]?.price,
      timestamp: Date.now()
    });
    
    // Limit items
    items = items.slice(0, MAX_ITEMS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.debug("Error saving recently viewed:", e);
  }
};

// Get recently viewed products
export const getRecentlyViewed = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Component to display recently viewed
const RecentlyViewed = ({ excludeProductId = null, maxItems = 4 }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let viewed = getRecentlyViewed();
    if (excludeProductId) {
      viewed = viewed.filter(p => p.product_id !== excludeProductId);
    }
    setItems(viewed.slice(0, maxItems));
  }, [excludeProductId, maxItems]);

  if (items.length === 0) return null;

  return (
    <section className="py-12" data-testid="recently-viewed-section">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-medium">Recently Viewed</h3>
        </div>
        <Link 
          to="/products" 
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          View All
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((product, index) => (
          <motion.div
            key={product.product_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link to={`/products/${product.product_id}`}>
              <Card className="group cursor-pointer rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square relative overflow-hidden bg-muted">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-3">
                  <h4 className="font-medium text-sm truncate">{product.title}</h4>
                  <p className="text-xs text-muted-foreground mt-1 capitalize">
                    {product.category?.replace("_", " ") || "Artwork"}
                  </p>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default RecentlyViewed;
